import { supabase } from './supabase';

export interface DesignSystem {
  id: string;
  workspace_id: string;
  name: string;
  description: string | null;
  typography_preset_id: string | null;
  color_preset_id: string | null;
  created_by: string;
  created_at: string;
  updated_at: string;
  // Joined data
  typography_preset?: any;
  color_preset?: any;
}

export interface CreateDesignSystemData {
  workspace_id: string;
  name: string;
  description?: string;
  id?: string;
}

export const designSystemService = {
  async getByWorkspace(workspaceId: string): Promise<DesignSystem[]> {
    const { data, error } = await supabase
      .from('design_systems')
      .select(`
        *,
        typography_preset:typography_presets(*),
        color_preset:color_presets(*)
      `)
      .eq('workspace_id', workspaceId)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data || [];
  },

  async getById(id: string): Promise<DesignSystem | null> {
    const { data, error } = await supabase
      .from('design_systems')
      .select(`
        *,
        typography_preset:typography_presets(*),
        color_preset:color_presets(*)
      `)
      .eq('id', id)
      .maybeSingle();

    if (error) throw error;
    return data;
  },

  async create(data: CreateDesignSystemData): Promise<DesignSystem> {
    const { data: userData } = await supabase.auth.getUser();
    if (!userData.user) throw new Error('User not authenticated');

    // 1. Create the design system record
    const insertData: any = {
      workspace_id: data.workspace_id,
      name: data.name,
      description: data.description || null,
      created_by: userData.user.id,
    };
    if (data.id) insertData.id = data.id;

    const { data: ds, error } = await supabase
      .from('design_systems')
      .insert(insertData)
      .select()
      .single();

    if (error) throw error;

    // 2. Create a default typography preset linked to this design system
    // Guarantee unique name per workspace to avoid 409 unique constraint violation
    let typPresetName = data.name;
    try {
      const { data: existingTyp } = await supabase
        .from('typography_presets')
        .select('name')
        .eq('workspace_id', data.workspace_id);

      if (existingTyp && existingTyp.length > 0) {
        const names = new Set(existingTyp.map(t => t.name));
        if (names.has(typPresetName)) {
          let counter = 2;
          while (names.has(`${data.name} ${counter}`)) {
            counter++;
          }
          typPresetName = `${data.name} ${counter}`;
        }
      }
    } catch {
      // Fall back if select fails
      typPresetName = `${data.name} (${Date.now().toString(36)})`;
    }

    const { data: typPreset, error: typError } = await supabase
      .from('typography_presets')
      .insert({
        workspace_id: data.workspace_id,
        name: typPresetName,
        description: '',
        naming_convention: 'text',
        min_font_size: 16,
        max_font_size: 18,
        min_scale_ratio: 1.125,
        max_scale_ratio: 1.333,
        base_scale_index: 'm',
        scale_mode: 'automatic',
        heading_font_family: 'Inter',
        body_font_family: 'Inter',
        heading_font_weight: '700',
        heading_line_height: 1.2,
        heading_letter_spacing: 0,
        body_font_weight: '400',
        body_line_height: 1.6,
        body_letter_spacing: 0,
        created_by: userData.user.id,
      })
      .select()
      .single();

    if (typError) throw typError;

    // 3. Create a default color preset with a starter palette
    let colorPresetName = data.name;
    try {
      const { data: existingColor } = await supabase
        .from('color_presets')
        .select('name')
        .eq('workspace_id', data.workspace_id);

      if (existingColor && existingColor.length > 0) {
        const names = new Set(existingColor.map(c => c.name));
        if (names.has(colorPresetName)) {
          let counter = 2;
          while (names.has(`${data.name} ${counter}`)) {
            counter++;
          }
          colorPresetName = `${data.name} ${counter}`;
        }
      }
    } catch {
      // Fall back if select fails
    }

    const defaultColors = [
      { id: crypto.randomUUID(), name: 'Primary', hex: '#6366F1', variable: '--color-primary', format: 'oklch' },
      { id: crypto.randomUUID(), name: 'Secondary', hex: '#EC4899', variable: '--color-secondary', format: 'oklch' },
      { id: crypto.randomUUID(), name: 'Accent', hex: '#14B8A6', variable: '--color-accent', format: 'oklch' },
      { id: crypto.randomUUID(), name: 'Neutral', hex: '#64748B', variable: '--color-neutral', format: 'oklch' },
      { id: crypto.randomUUID(), name: 'Background', hex: '#F8FAFC', variable: '--color-background', format: 'oklch' },
    ];

    const { data: colorPreset, error: colorError } = await supabase
      .from('color_presets')
      .insert({
        workspace_id: data.workspace_id,
        name: colorPresetName,
        category: 'Custom',
        colors: defaultColors,
        is_template: false,
        created_by: userData.user.id,
      })
      .select()
      .single();

    if (colorError) throw colorError;

    // 4. Link the presets to the design system
    const { data: updated, error: updateError } = await supabase
      .from('design_systems')
      .update({
        typography_preset_id: typPreset.id,
        color_preset_id: colorPreset.id,
      })
      .eq('id', ds.id)
      .select(`
        *,
        typography_preset:typography_presets(*),
        color_preset:color_presets(*)
      `)
      .single();

    if (updateError) throw updateError;
    return updated;
  },

  async update(id: string, updates: { name?: string; description?: string }): Promise<DesignSystem> {
    const { data, error } = await supabase
      .from('design_systems')
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq('id', id)
      .select(`
        *,
        typography_preset:typography_presets(*),
        color_preset:color_presets(*)
      `)
      .single();

    if (error) throw error;
    return data;
  },

  async delete(id: string): Promise<void> {
    // First get the design system to find linked preset IDs
    const ds = await this.getById(id);
    if (!ds) throw new Error('Design system not found');

    // Delete the design system (this doesn't cascade to presets due to SET NULL)
    const { error } = await supabase
      .from('design_systems')
      .delete()
      .eq('id', id);

    if (error) throw error;

    // Clean up orphaned presets
    if (ds.typography_preset_id) {
      await supabase.from('typography_presets').delete().eq('id', ds.typography_preset_id);
    }
    if (ds.color_preset_id) {
      await supabase.from('color_presets').delete().eq('id', ds.color_preset_id);
    }
  },

  async duplicate(id: string): Promise<DesignSystem> {
    const ds = await this.getById(id);
    if (!ds) throw new Error('Design system not found');

    const { data: userData } = await supabase.auth.getUser();
    if (!userData.user) throw new Error('User not authenticated');

    // Duplicate typography preset if exists
    let newTypPresetId: string | null = null;
    if (ds.typography_preset) {
      const { id: _id, created_at: _ca, updated_at: _ua, ...typData } = ds.typography_preset;
      const { data: newTyp, error } = await supabase
        .from('typography_presets')
        .insert({ ...typData, name: `${typData.name} (Copy)`, created_by: userData.user.id })
        .select()
        .single();
      if (!error && newTyp) newTypPresetId = newTyp.id;
    }

    // Duplicate color preset if exists
    let newColorPresetId: string | null = null;
    if (ds.color_preset) {
      const { data: newColor, error } = await supabase
        .from('color_presets')
        .insert({
          workspace_id: ds.workspace_id,
          name: `${ds.color_preset.name} (Copy)`,
          category: ds.color_preset.category || 'Custom',
          colors: ds.color_preset.colors || [],
          is_template: false,
          created_by: userData.user.id,
        })
        .select()
        .single();
      if (!error && newColor) newColorPresetId = newColor.id;
    }

    // Create duplicate design system
    const { data: newDs, error: dsError } = await supabase
      .from('design_systems')
      .insert({
        workspace_id: ds.workspace_id,
        name: `${ds.name} (Copy)`,
        description: ds.description,
        typography_preset_id: newTypPresetId,
        color_preset_id: newColorPresetId,
        created_by: userData.user.id,
      })
      .select(`
        *,
        typography_preset:typography_presets(*),
        color_preset:color_presets(*)
      `)
      .single();

    if (dsError) throw dsError;
    return newDs;
  },
};
